import { useCallback, useState } from 'react';
import type { UserEntity } from '@user/UserEntity';
import { FlatButton } from '@framework/ui/atoms';
import { Modal } from '@framework/ui/elements';
import { EditPasswordForm, PasswordInput } from '@user/EditPasswordForm';
import { FirebaseAuthenticationClient } from '@framework/firebase/auth';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { trpcClient } from '@framework/trpcClient';
import { Trans, useTranslation } from 'react-i18next';

type ResetLinkProps = { children?: React.ReactNode };
const ResetLink: React.FC<ResetLinkProps> = ({ children }: ResetLinkProps) => (
  <a
    href={UserPagePathBuilder.passwordResetPage()}
    target="_blank"
    rel="noreferrer"
    className="text-blue-700 hover:underline"
  >
    {children} <FontAwesomeIcon icon={faExternalLinkAlt} />
  </a>
);

type Props = {
  isOpen: boolean;
  onClose(): void;
  userEntity: UserEntity;
};

export const PasswordChangeModal: React.FC<Props> = ({ isOpen, onClose, userEntity }: Props) => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState('');

  const handleChangeCurrentPassword = useCallback((password: string) => setCurrentPassword(password), []);
  const handleChangeNewPassword = useCallback(
    (newPassword: string) => {
      setNewPassword(newPassword);

      if (newPassword && currentPassword == '') {
        setCurrentPasswordError(t('auth.passwordChange.modal.currentPasswordRequired'));
      }
    },
    [currentPassword, t]
  );
  const validateOnBlur = useCallback(() => {
    if (currentPassword === '') {
      setCurrentPasswordError(t('auth.passwordChange.modal.currentPasswordRequired'));
    } else {
      setCurrentPasswordError('');
    }
  }, [currentPassword, t]);

  const isEmpty = currentPassword === '' || newPassword === '';

  const handleClose = useCallback(() => {
    setProcessing(false);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (currentPassword === '') return;
    if (newPassword === '') return;

    setProcessing(true);
    setError('');
    setCurrentPasswordError('');

    try {
      const { customToken } = await trpcClient.user.auth.changePassword.mutate({ currentPassword, newPassword });

      // パスワード変更(Admin SDK)は Firebase Auth の validSince を進め、現在のセッションの
      // ID/refresh トークンを失効させる。放置すると次のトークンリフレッシュ等のタイミングで
      // auth/user-token-expired により強制サインアウトされるため、同一セッションを引き継いだ
      // カスタムトークンで再サインインしてトークンを張り直す。
      // 再サインインの失敗はパスワード変更自体の失敗ではないためエラー表示にはしない。
      if (customToken) {
        await FirebaseAuthenticationClient.signInWithCustomToken(customToken).catch((error) => console.error(error));
      }

      toast.success(t('auth.passwordChanged.message'));
      handleClose();
    } catch (error) {
      setError(t('auth.passwordChange.modal.changeError'));
      console.error('Failed update password:', error);
    } finally {
      setProcessing(false);
    }
  }, [currentPassword, handleClose, newPassword, t]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="flex flex-col">
      {/* header */}
      <div className="px-8 pb-4 pt-8 text-2xl font-bold">{t('auth.passwordChange.modal.title')}</div>
      <hr className="w-full" />

      {/* content */}
      <div className="flex flex-col p-8">
        <div className="pb-2 font-bold">{t('auth.signUpConfirm.emailLabel')}</div>
        <input
          type="text"
          className="cursor-not-allowed rounded-sm border bg-gray-200 p-2"
          defaultValue={userEntity.mailAddress}
          disabled
        />

        <div className="pb-2 pt-8 font-bold">{t('auth.passwordChange.modal.currentPasswordLabel')}</div>
        <PasswordInput
          value={currentPassword}
          onChange={handleChangeCurrentPassword}
          onBlur={validateOnBlur}
          error={currentPasswordError}
          autoFocus
          disabled={processing}
        />
        <div className="pt-1 text-xs">
          <Trans i18nKey="auth.passwordChange.modal.forgotNote" components={{ resetLink: <ResetLink /> }} />
        </div>

        <EditPasswordForm onChange={handleChangeNewPassword} disabled={processing} />

        {error && <div className="pt-8 text-red-700">{error}</div>}
      </div>

      {/* footer */}
      <hr className="w-full" />
      <div className="flex justify-end px-8 pb-8 pt-4">
        <FlatButton onClick={handleClose}>{t('common.action.close')}</FlatButton>
        <FlatButton
          color="brand"
          onClick={handleSubmit}
          className="ml-2 px-8"
          disabled={isEmpty || processing}
          loading={processing}
        >
          {t('common.action.save')}
        </FlatButton>
      </div>
    </Modal>
  );
};
